import React from 'react';
import { Button, Chip, Typography, Card } from '@mui/material';
import BackButton from '../BackButton';
import Future from "../../../../../../assets/images/ArticleImages/future.webp";
import HowConnect from "../../../../../../assets/images/ArticleImages/howConnect.webp";
import KeyComponents from "../../../../../../assets/images/ArticleImages/Key-Components.webp";
import KeyUseCases from "../../../../../../assets/images/ArticleImages/Key-Use-Cases.webp";
import ImpactOf5G from "../../../../../../assets/images/ArticleImages/Impact-of-5g-on.webp";
import SmartSolution from "../../../../../../assets/images/ArticleImages/Smart-Solutions-for-Connected-Vehicle.webp";

// Define a type for the content blocks
type ContentBlock =
  | { type: 'heading'; value: string; variant?: 'h4' | 'h5' | 'h6'; style?: React.CSSProperties }
  | { type: 'paragraph'; value: string; style?: React.CSSProperties }
  | { type: 'image'; src: string; alt?: string; style?: React.CSSProperties };

const VehiclesSmartMobility: React.FC = () => {
  // Page header information
  const title = "Future of Connected Vehicles & Smart Mobility";
  const headerImageSrc = Future;

  // Define the content of your article as a list of blocks.
  const contentBlocks: ContentBlock[] = [
    {
      type: 'paragraph',
      value: "Transportation systems across the world are rapidly changing, with connected vehicles playing a central role in shaping the future of mobility. Connected vehicle technology helps improve safety, efficiency, and automation. It affects how vehicles speak with each other, the infrastructure, and the cloud system. Connected vehicles provide numerous benefits which lessen traffic congestion and enable efficient predictive maintenance.",
    },
    {
      type: 'heading',
      value: "How Connected Vehicle Technology Works",
      variant: 'h6',
      style: { marginTop: '16px', marginBottom: '8px' },
    },
    {
      type: 'image',
      src: HowConnect,
      alt: "Connected vehicles schematic",
      style: { margin: '24px 0', width: '100%', height: 'auto', borderRadius: 12 },
    },
    {
      type: 'paragraph',
      value: "The technology related to connected cars consists of complex systems that allow data sharing and decision-making at the same time.",
    },
    {
      type: 'paragraph',
      value: (
        <>
          <strong>Sensors:</strong> Several sensors are fitted inside the vehicles in order to collect data related to the vehicle’s speed, location, road conditions, etc.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          <strong>Data processing:</strong> To process data as efficiently as possible, vehicles use both onboard computing units and cloud computing.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          <strong>Artificial intelligence (AI) and machine learning: </strong> enable the vehicle to use data to make safe decisions as well as enhance its overall functioning.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          <strong>V2X communication:</strong> is the communication between a vehicle and everything around it. Through this process, vehicles can communicate with other vehicles, the infrastructure, and pedestrians.
        </>
      ) as unknown as string,
    },
    {
      type: 'heading',
      value: "Key Components of Connected Vehicles",
      variant: 'h6',
      style: { marginTop: '16px', marginBottom: '8px' },
    },
    {
      type: 'image',
      src: KeyComponents,
      alt: "Connected vehicles schematic",
      style: { margin: '24px 0', width: '100%', height: 'auto', borderRadius: 12 },
    },
    {
      type: 'paragraph',
      value: "To appreciate connected vehicle technology, it is indeed worth understanding its basic components.",
    },
    {
      type: 'paragraph',
      value: (
        <>
          1.<strong> Vehicle To Vehicle (V2V) Communication:</strong> This is the real-time transfer of safety messages such as speed, location, and braking status. These messages actively support crash prevention, better traffic flow management, and road safety.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          2.<strong> Vehicle-to-Infrastructure (V2I) Communication:</strong> This message enhances your experience. This is a message from and to traffic lights, signs, and other infrastructure that communicate with vehicles. V2I messages provide real-time updates on traffic conditions, hazards on the road, and the best route.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          3.<strong> Cloud Computing & IoT Integration:</strong> The data of the connected vehicle is sent to the cloud where it is analyzed for insights about navigation, fleet management, and predictive maintenance.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          4.<strong> AI and Machine Learning:</strong> AI systems help make real-time decisions smarter by planning efficient routes, preventing accidents, and personalizing driving experiences.
        </>
      ) as unknown as string,
    },
    {
      type: 'heading',
      value: "Key Use Cases of Connected Vehicles",
      variant: 'h6',
      style: { marginTop: '16px', marginBottom: '8px' },
    },
    {
      type: 'image',
      src: KeyUseCases,
      alt: "Connected vehicles schematic",
      style: { margin: '24px 0', width: '100%', height: 'auto', borderRadius: 12 },
    },
    {
      type: 'paragraph',
      value: "Connected vehicles have a variety of services that can contribute to mobility, efficiency, and safety:",
    },
    {
      type: 'paragraph',
      value: (
        <>
          1.<strong> Traffic Management and Congestion Management:</strong> Real-time data from connected vehicles can be used to optimize the timing of traffic signals, re-direct vehicles to lower the risk of congestion, and re-route vehicles to avoid congested high-volume situations.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          2.<strong> Fleet Optimization and Logistics:</strong> Connected vehicle technologies provide commercial transportation companies with the ability to track their vehicles in real-time, monitor driver behaviour, and optimize service routines to gain efficiencies.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          3.<strong> Predictive Maintenance:</strong> With the help of IoT, attributes of vehicle health can be monitored in real-time, such as oil levels and tire pressures, and give notice of maintenance issues before they become urgent. This can lower repair costs, extend vehicle use prior to needing maintenance, and reduce time out of service.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          4.<strong> Driver Assistance and Safety Features:</strong> Connected vehicles are often equipped with Advanced Driver Assistance Systems (ADAS) capabilities such as lane-keeping assistance, collision warnings, and emergency braking that uniquely reduce the rate of accidents.
        </>
      ) as unknown as string,
    },
    {
      type: 'heading',
      value: "Impact of 5G on Connected Vehicles",
      variant: 'h6',
      style: { marginTop: '16px', marginBottom: '8px' },
    },
    {
      type: 'image',
      src: ImpactOf5G,
      alt: "Connected vehicles schematic",
      style: { margin: '24px 0', width: '100%', height: 'auto', borderRadius: 12 },
    },
    {
      type: 'paragraph',
      value: "The installation of 5G networks will improve the technology of connected vehicles through better connectivity and reduced latency.",
    },
    {
      type: 'paragraph',
      value: "Here’s how:",
    },
    {
      type: 'paragraph',
      value: (
        <>
          1.<strong> Faster Data Transmission:</strong> 5G enables ultra-low latency communication, ensuring vehicles can process and share real-time data instantaneously.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          2.<strong> Better Support:</strong> For Self-Driving Cars With faster and more reliable connectivity, autonomous vehicles can access high-precision mapping and make split-second driving decisions.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          3.<strong> Greater Safety and Up–to–Date Messages:</strong> Connected cars with 5G technology can communicate real-time accident alerts and emergency messages to each other.
        </>
      ) as unknown as string,
    },
    {
      type: 'heading',
      value: "Connected Vehicles vs. Automated Vehicles",
      variant: 'h6',
      style: { marginTop: '16px', marginBottom: '8px' },
    },
    {
      type: 'paragraph',
      value: "Connected Vehicles and Automated Vehicle systems are different connected vehicles that depend on data from external networks while still requiring human control.",
    },
    {
      type: 'paragraph',
      value: (
        <>
          1. Autonomous vehicles operate independently using <strong>AI driverless technology</strong> and do not require human control.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          2.<strong> Connected vehicles and autonomous vehicles</strong> (driverless car technology) work well together, but full automation will require the two to work together.
        </>
      ) as unknown as string,
    },
    {
      type: 'heading',
      value: "Challenges in Connected Vehicle Technology",
      variant: 'h6',
      style: { marginTop: '16px', marginBottom: '8px' },
    },
    {
      type: 'paragraph',
      value: "Connected vehicle technology is useful and handy but has its fair share of challenges.",
    },
    {
      type: 'paragraph',
      value: (
        <>
          1.<strong> Cybersecurity risks</strong> Since vehicles send huge amounts of data, cyber threats like hacking and data breaches pose great risks.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          2.<strong> User data</strong> is continuously collected which causes concerns on how it is stored, shared, or used by third-party bodies.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          3.<strong> Infrastructure & Deployment Barriers:</strong> In order for connected vehicle technology to work on a large scale, major investments are needed in smart road infrastructure and 5G deployment.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          4.<strong> Different areas have different vehicle regulations</strong> that hinder the creation of universal standards for connected car regulations and cyber threats.
        </>
      ) as unknown as string,
    },
    {
      type: 'heading',
      value: "Novus Hi-Tech – Smart Solutions for Connected Vehicles",
      variant: 'h6',
      style: { marginTop: '16px', marginBottom: '8px' },
    },
    {
      type: 'image',
      src: SmartSolution,
      alt: "Connected vehicles schematic",
      style: { margin: '24px 0', width: '100%', height: 'auto', borderRadius: 12 },
    },
    {
      type: 'paragraph',
      value: "As a leader in connected vehicle technology, Novus Hi-Tech offers cutting-edge solutions to drive the future of smart mobility.",
    },
    {
      type: 'paragraph',
      value: (
        <>
          1.<strong> Advanced Driver Assistance Systems (ADAS):</strong> Novus Hi-Tech’s ADAS solutions enhance driver safety through real-time monitoring, lane departure warnings, and collision avoidance systems.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          2.<strong> IoT & AI Integration for Smart Vehicles:</strong> By integrating AI and IoT, Novus Hi-Tech enables efficient fleet management, real-time diagnostics, and predictive maintenance.
        </>
      ) as unknown as string,
    },
    {
      type: 'paragraph',
      value: (
        <>
          3.<strong> Fleet Management & Predictive Maintenance Solutions:</strong> Novus Hi-Tech’s smart fleet solutions offer real-time insights into vehicle health, optimizing operations for logistics and transportation companies.
        </>
      ) as unknown as string,
    },
    {
      type: 'heading',
      value: "Conclusion",
      variant: 'h6',
      style: { marginTop: '16px', marginBottom: '8px' },
    },
    {
      type: 'paragraph',
      value: "There is a huge potential for connected vehicles in the future as technology is always evolving to enhance road safety, traffic efficiency, and fleet management. The infusion of 5G, AI, and IoT technology in connected vehicles will continue to augment their efficiency and network capability. Thus, we will slowly move towards a smart, safe, and efficient mobility ecosystem. Firms such as Novus Hi-Tech are critically important in this transformation by providing innovative solutions that drive the mobility of the future.",
    },
  ];

  // Function to render each content block based on its type.
  const renderContentBlock = (block: ContentBlock, index: number) => {
    switch (block.type) {
      case 'heading':
        return (
          <Typography
            key={index}
            variant={block.variant || 'h4'}
            style={{ fontWeight: 600, ...block.style }}
          >
            {block.value}
          </Typography>
        );
      case 'paragraph':
        return (
          <Typography
            key={index}
            variant="body1"
            style={{
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
              // color: 'gray',
              ...block.style,
            }}
          >
            {block.value}
          </Typography>
        );
      case 'image':
        return (
          <Card
            key={index}
            style={{
              overflow: 'hidden',
              borderRadius: 12,
              ...block.style,
            }}
          >
            <img
              src={block.src}
              alt={block.alt || 'Article image'}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col w-full">
      <BackButton />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center' }}>
        {/* Header Section */}
        <div
          style={{
            width: '100%',
            maxWidth: '766px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {/* Title & Tag Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography variant="h4" style={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
            </div>
          </div>

          {/* Header Image */}
          <Card
            style={{
              width: '100%',
              height: 384,
              borderRadius: 12,
              overflow: 'hidden',
            }}
          >
            <img
              src={headerImageSrc}
              alt="Article header image"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Card>
        </div>

        {/* Content Section */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%',
            maxWidth: '766px',
            padding: '16px 0',
          }}
        >
          {contentBlocks.map((block, index) => renderContentBlock(block, index))}
        </div>
      </div>
    </div>
  );
};

export default VehiclesSmartMobility;
